/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { css } from 'aphrodite';
import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { useConsumerTheme } from '../../../../hooks/useConsumerTheme';
import { addGenericErrorNotification } from '../../../../actions/pageActions';
import { diagnostics } from '@utils/diagnostics';

interface VideoTrackProps {
  participant: VideoProvider.IParticipant;
}

const generateStyles = () => ({
  videoPlayer: {
    width: '100%',
  },
});

const ZoomVideoTrack: React.FC<VideoTrackProps> = ({ participant }) => {
  const dispatch = useDispatch();
  const { styles } = useConsumerTheme(generateStyles);
  const { room, videoProvider } = useVideoContext();
  const [isVideoAttached, setIsVideoAttached] = useState(false);
  const videoPlayerRef = useRef<VideoProvider.ZoomVideoPlayer | null>(null);

  useEffect(() => {
    if (!participant || !room || !videoPlayerRef.current || !participant.bVideoOn || isVideoAttached) {
      return;
    }
    const startVideo = async () => {
      try {
        await videoProvider.attachVideoToVideoPlayer(participant.userId, videoPlayerRef.current!);
        setIsVideoAttached(true);
      } catch (err: any) {
        // NOTE: (ZOOMSDK) This can be safetely ignored as their response in forum it is expected behaviour
        // https://devforum.zoom.us/t/getting-play-request-error-on-rendering-self-video-via-video-player-container/107156
        const errMessage = 'Failed to attach Zoom video';
        diagnostics.error(new Error(errMessage), err);
        addGenericErrorNotification('Unable to load the video tile. Please refresh the page and try again.')(dispatch);
      }
    };

    startVideo();
  }, [participant, room]);

  useEffect(() => {
    const endVideo = async () => {
      try {
        await videoProvider.detachVideoFromVideoPlayer(participant.userId);
      } catch (err: any) {
        const errMessage = 'Failed to detach Zoom video';
        diagnostics.error(new Error(errMessage), err);
      }
    };
    return () => {
      setIsVideoAttached(false);
      endVideo();
    };
  }, []);

  return <video-player ref={videoPlayerRef} class={css(styles.videoPlayer)} />;
};

export const MemoizedZoomVideoTrack = React.memo(ZoomVideoTrack);
